/**
 * useClientFitnessProfile Hook
 *
 * Manages client fitness profiles including:
 * - Training background and experience
 * - Goals and objectives
 * - Available equipment
 * - Injuries and movement restrictions
 * - FMS/PAR-Q assessment data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Training experience levels
export type TrainingExperience = 'beginner' | 'intermediate' | 'advanced' | 'elite';

// Primary training goals
export type PrimaryGoal =
  | 'strength'
  | 'hypertrophy'
  | 'fat_loss'
  | 'endurance'
  | 'athletic_performance'
  | 'general_fitness'
  | 'rehabilitation';

// Training location types
export type TrainingLocation = 'commercial_gym' | 'home_gym' | 'minimal' | 'outdoor';

// PAR-Q risk levels
export type ParqRiskLevel = 'none' | 'low' | 'moderate' | 'high';

// Injury record
export interface Injury {
  area: string;
  severity: 'minor' | 'moderate' | 'severe';
  notes?: string;
  avoid_exercises?: string[];
  start_date?: string;
  is_active: boolean;
}

// Movement restriction
export interface MovementRestriction {
  pattern: string;
  severity: 'minor' | 'moderate' | 'severe';
  notes?: string;
}

// Exercise preferences
export interface ExercisePreferences {
  liked: string[];
  disliked: string[];
}

// FMS asymmetry data
export interface FmsAsymmetry {
  left: number;
  right: number;
  asymmetry: boolean;
}

// Complete fitness profile
export interface ClientFitnessProfile {
  id: string;
  client_id: string;
  organization_id: string | null;

  // Training background
  training_experience: TrainingExperience | null;
  training_years: number | null;
  current_training_frequency: number | null;
  preferred_session_duration: number | null;

  // Goals
  primary_goal: PrimaryGoal | null;
  secondary_goals: string[];
  goal_notes: string | null;

  // Equipment
  available_equipment: string[];
  training_location: TrainingLocation | null;

  // Limitations
  injuries: Injury[];
  movement_restrictions: MovementRestriction[];
  exercise_preferences: ExercisePreferences;

  // Assessment data
  fms_total_score: number | null;
  fms_asymmetries: Record<string, FmsAsymmetry>;
  parq_risk_level: ParqRiskLevel | null;
  medical_clearance_required: boolean;

  // AI metadata
  last_ai_analysis_at: string | null;
  ai_recommendations: Record<string, unknown>;

  created_at: string;
  updated_at: string;
}

// Input for creating/updating a profile
export interface FitnessProfileInput {
  training_experience?: TrainingExperience | null;
  training_years?: number | null;
  current_training_frequency?: number | null;
  preferred_session_duration?: number | null;
  primary_goal?: PrimaryGoal | null;
  secondary_goals?: string[];
  goal_notes?: string | null;
  available_equipment?: string[];
  training_location?: TrainingLocation | null;
  injuries?: Injury[];
  movement_restrictions?: MovementRestriction[];
  exercise_preferences?: ExercisePreferences;
  fms_total_score?: number | null;
  fms_asymmetries?: Record<string, FmsAsymmetry>;
  parq_risk_level?: ParqRiskLevel | null;
  medical_clearance_required?: boolean;
}

// Standard equipment categories for selection
export const EQUIPMENT_CATEGORIES = {
  barbells: ['barbell', 'olympic_barbell', 'ez_curl_bar', 'trap_bar'],
  dumbbells: ['dumbbells', 'adjustable_dumbbells'],
  machines: [
    'cable_machine',
    'smith_machine',
    'leg_press',
    'chest_press_machine',
    'lat_pulldown',
    'seated_row_machine',
    'leg_curl',
    'leg_extension',
    'hack_squat',
  ],
  cardio: ['treadmill', 'elliptical', 'stationary_bike', 'rowing_machine', 'stair_climber'],
  bodyweight: ['pull_up_bar', 'dip_station', 'gymnastics_rings'],
  resistance: ['resistance_bands', 'cable_attachments'],
  kettlebells: ['kettlebells'],
  benches: ['flat_bench', 'incline_bench', 'decline_bench', 'adjustable_bench'],
  racks: ['squat_rack', 'power_rack', 'half_rack'],
  accessories: ['ab_roller', 'foam_roller', 'medicine_ball', 'stability_ball', 'battle_ropes'],
} as const;

// Flat list of all equipment
export const ALL_EQUIPMENT = Object.values(EQUIPMENT_CATEGORIES).flat();

interface UseClientFitnessProfileOptions {
  clientId: string;
}

/**
 * Hook to manage client fitness profiles
 */
export function useClientFitnessProfile({ clientId }: UseClientFitnessProfileOptions) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch the client's fitness profile
  const profileQuery = useQuery({
    queryKey: ['client-fitness-profile', clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_fitness_profiles')
        .select('*')
        .eq('client_id', clientId)
        .maybeSingle();

      if (error) throw error;

      if (!data) return null;

      // Parse JSONB fields with defaults
      return {
        ...data,
        injuries: Array.isArray(data.injuries) ? data.injuries : [],
        movement_restrictions: Array.isArray(data.movement_restrictions)
          ? data.movement_restrictions
          : [],
        exercise_preferences: data.exercise_preferences || { liked: [], disliked: [] },
        fms_asymmetries: data.fms_asymmetries || {},
        ai_recommendations: data.ai_recommendations || {},
        secondary_goals: data.secondary_goals || [],
        available_equipment: data.available_equipment || [],
      } as ClientFitnessProfile;
    },
    enabled: !!clientId,
  });

  // Create a new fitness profile
  const createProfile = useMutation({
    mutationFn: async (input: FitnessProfileInput) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user) throw new Error('Not authenticated');

      // Get the client's organization_id
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .select('organization_id')
        .eq('id', clientId)
        .single();

      if (clientError) throw clientError;

      const { data, error } = await supabase
        .from('client_fitness_profiles')
        .insert({
          client_id: clientId,
          organization_id: client.organization_id,
          training_experience: input.training_experience ?? null,
          training_years: input.training_years ?? null,
          current_training_frequency: input.current_training_frequency ?? null,
          preferred_session_duration: input.preferred_session_duration ?? null,
          primary_goal: input.primary_goal ?? null,
          secondary_goals: input.secondary_goals ?? [],
          goal_notes: input.goal_notes ?? null,
          available_equipment: input.available_equipment ?? [],
          training_location: input.training_location ?? null,
          injuries: input.injuries ?? [],
          movement_restrictions: input.movement_restrictions ?? [],
          exercise_preferences: input.exercise_preferences ?? { liked: [], disliked: [] },
          fms_total_score: input.fms_total_score ?? null,
          fms_asymmetries: input.fms_asymmetries ?? {},
          parq_risk_level: input.parq_risk_level ?? null,
          medical_clearance_required: input.medical_clearance_required ?? false,
        })
        .select()
        .single();

      if (error) throw error;
      return data as ClientFitnessProfile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-fitness-profile', clientId] });
      toast({
        title: 'Profile created',
        description: 'Fitness profile has been saved.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update an existing fitness profile
  const updateProfile = useMutation({
    mutationFn: async (input: FitnessProfileInput) => {
      const updateData: Record<string, unknown> = {};

      // Only include fields that are explicitly provided
      if (input.training_experience !== undefined)
        updateData.training_experience = input.training_experience;
      if (input.training_years !== undefined) updateData.training_years = input.training_years;
      if (input.current_training_frequency !== undefined)
        updateData.current_training_frequency = input.current_training_frequency;
      if (input.preferred_session_duration !== undefined)
        updateData.preferred_session_duration = input.preferred_session_duration;
      if (input.primary_goal !== undefined) updateData.primary_goal = input.primary_goal;
      if (input.secondary_goals !== undefined) updateData.secondary_goals = input.secondary_goals;
      if (input.goal_notes !== undefined) updateData.goal_notes = input.goal_notes;
      if (input.available_equipment !== undefined)
        updateData.available_equipment = input.available_equipment;
      if (input.training_location !== undefined)
        updateData.training_location = input.training_location;
      if (input.injuries !== undefined) updateData.injuries = input.injuries;
      if (input.movement_restrictions !== undefined)
        updateData.movement_restrictions = input.movement_restrictions;
      if (input.exercise_preferences !== undefined)
        updateData.exercise_preferences = input.exercise_preferences;
      if (input.fms_total_score !== undefined) updateData.fms_total_score = input.fms_total_score;
      if (input.fms_asymmetries !== undefined) updateData.fms_asymmetries = input.fms_asymmetries;
      if (input.parq_risk_level !== undefined) updateData.parq_risk_level = input.parq_risk_level;
      if (input.medical_clearance_required !== undefined)
        updateData.medical_clearance_required = input.medical_clearance_required;

      const { data, error } = await supabase
        .from('client_fitness_profiles')
        .update(updateData)
        .eq('client_id', clientId)
        .select()
        .single();

      if (error) throw error;
      return data as ClientFitnessProfile;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-fitness-profile', clientId] });
      toast({
        title: 'Profile updated',
        description: 'Changes have been saved.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Upsert profile (create if not exists, update if exists)
  const upsertProfile = useMutation({
    mutationFn: async (input: FitnessProfileInput) => {
      const existing = profileQuery.data;
      if (existing) {
        return updateProfile.mutateAsync(input);
      } else {
        return createProfile.mutateAsync(input);
      }
    },
  });

  // Add an injury
  const addInjury = useMutation({
    mutationFn: async (injury: Injury) => {
      const currentInjuries = profileQuery.data?.injuries || [];
      const updatedInjuries = [...currentInjuries, injury];

      return updateProfile.mutateAsync({ injuries: updatedInjuries });
    },
  });

  // Remove an injury by index
  const removeInjury = useMutation({
    mutationFn: async (index: number) => {
      const currentInjuries = profileQuery.data?.injuries || [];
      const updatedInjuries = currentInjuries.filter((_, i) => i !== index);

      return updateProfile.mutateAsync({ injuries: updatedInjuries });
    },
  });

  // Update equipment list
  const updateEquipment = useMutation({
    mutationFn: async (equipment: string[]) => {
      return updateProfile.mutateAsync({ available_equipment: equipment });
    },
  });

  // Update goals
  const updateGoals = useMutation({
    mutationFn: async ({
      primary,
      secondary,
      notes,
    }: {
      primary?: PrimaryGoal | null;
      secondary?: string[];
      notes?: string;
    }) => {
      return updateProfile.mutateAsync({
        primary_goal: primary,
        secondary_goals: secondary,
        goal_notes: notes,
      });
    },
  });

  return {
    profile: profileQuery.data,
    isLoading: profileQuery.isLoading,
    error: profileQuery.error,
    createProfile,
    updateProfile,
    upsertProfile,
    addInjury,
    removeInjury,
    updateEquipment,
    updateGoals,
    refetch: profileQuery.refetch,
  };
}

/**
 * Hook to get fitness profiles for multiple clients (for batch operations)
 */
export function useClientFitnessProfiles(clientIds: string[]) {
  return useQuery({
    queryKey: ['client-fitness-profiles', clientIds],
    queryFn: async () => {
      if (!clientIds.length) return [];

      const { data, error } = await supabase
        .from('client_fitness_profiles')
        .select('*')
        .in('client_id', clientIds);

      if (error) throw error;

      return (data || []).map((profile) => ({
        ...profile,
        injuries: Array.isArray(profile.injuries) ? profile.injuries : [],
        movement_restrictions: Array.isArray(profile.movement_restrictions)
          ? profile.movement_restrictions
          : [],
        exercise_preferences: profile.exercise_preferences || { liked: [], disliked: [] },
        fms_asymmetries: profile.fms_asymmetries || {},
        ai_recommendations: profile.ai_recommendations || {},
        secondary_goals: profile.secondary_goals || [],
        available_equipment: profile.available_equipment || [],
      })) as ClientFitnessProfile[];
    },
    enabled: clientIds.length > 0,
  });
}

/**
 * Helper to check if a client has completed their fitness profile
 */
export function isProfileComplete(profile: ClientFitnessProfile | null): boolean {
  if (!profile) return false;

  return !!(
    profile.training_experience &&
    profile.primary_goal &&
    profile.training_location &&
    profile.available_equipment.length > 0
  );
}

/**
 * Helper to get completion percentage of fitness profile
 */
export function getProfileCompletionPercentage(profile: ClientFitnessProfile | null): number {
  if (!profile) return 0;

  const fields = [
    profile.training_experience,
    profile.training_years,
    profile.current_training_frequency,
    profile.preferred_session_duration,
    profile.primary_goal,
    profile.training_location,
    profile.available_equipment.length > 0,
  ];

  const completed = fields.filter(Boolean).length;
  return Math.round((completed / fields.length) * 100);
}
